import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiUser } from "react-icons/fi"

import { UsersService } from "@/client"
import type { UserCreate, UserUpdate } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import Appearance from "@/components/UserSettings/Appearance"
import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Checkbox } from "@/components/ui/checkbox"
import { Field } from "@/components/ui/field"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/user-form")({
  component: UserForm,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      userId: search.userId as string | undefined,
    }
  },
})

interface FormData {
  email: string
  full_name: string
  password?: string
  password_confirm?: string
  is_active: boolean
  is_superuser: boolean
}

function UserForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const { userId } = useSearch({ from: "/_layout/user-form" })
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common", "settings"])
  const { user: currentUser } = useAuth()
  const isEditMode = !!userId
  const isEditingSelf = isEditMode && currentUser?.id === userId
  const [confirmUnsuperuser, setConfirmUnsuperuser] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      is_active: true,
      is_superuser: false,
    },
  })

  // Register is_superuser manually since we use setValue instead of register on the checkbox
  register("is_superuser")

  const password = watch("password")
  const isSuperuser = watch("is_superuser")

  // Fetch user data if in edit mode
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => UsersService.readUser({ id: userId! }),
    enabled: isEditMode,
  })

  // Reset form when user data is loaded
  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        full_name: user.full_name || "",
        is_active: user.is_active ?? true,
        is_superuser: user.is_superuser ?? false,
      })
    }
  }, [user, reset])

  const createUserMutation = useMutation({
    mutationFn: async (data: UserCreate) => {
      const result = await UsersService.createUser({ requestBody: data })
      return result
    },
    onSuccess: (newUser) => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      showToast.showSuccessToast(t("admin:userForm.created"))
      // Navigate to permissions page to assign organizations
      navigate({
        to: "/user-permissions/$userId",
        params: { userId: newUser.id },
      })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to create user"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async (data: UserUpdate) => {
      const result = await UsersService.updateUser({
        id: userId!,
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["user", userId] })
      showToast.showSuccessToast(t("admin:userForm.updated"))
      navigate({ to: "/user-management" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update user"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleFormSubmit = (data: FormData) => {
    if (isEditMode) {
      // In edit mode, only send changed fields
      const updateData: UserUpdate = {
        email: data.email,
        full_name: data.full_name || null,
        is_active: data.is_active,
        is_superuser: data.is_superuser,
      }

      // Only include password if it was changed
      if (data.password) {
        updateData.password = data.password
      }

      updateUserMutation.mutate(updateData)
    } else {
      // In create mode, password is required
      const { password_confirm, ...userData } = data
      createUserMutation.mutate(userData as UserCreate)
    }
  }

  if (isEditMode && userLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={8}>
        <Box flex={1}>
          <Heading size="xl" color={sc.heading}>
            {isEditMode
              ? t("admin:userForm.editTitle")
              : t("admin:userForm.addTitle")}
          </Heading>
          <Text color={sc.textMuted} mt={1}>
            {isEditMode
              ? t("admin:userForm.editSubtitle")
              : t("admin:userForm.addSubtitle")}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/user-management" })}
          size="sm"
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", lg: "3fr 1fr" }}
        gap={6}
        as="form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Box>
          <GradientBorder borderRadius="20px" p={0}>
            <VisionCard variant="elevated" p={8} borderRadius="20px">
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiUser fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("admin:userForm.configTitle")}
                </Heading>
              </Flex>

              <VStack gap={6}>
                <VStack w="100%" gap={4}>
                  <Field
                    required
                    invalid={!!errors.email}
                    errorText={errors.email?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("admin:userForm.email")}
                    </Text>
                    <VisionInput
                      {...register("email", {
                        required: t("common:validation.emailRequired"),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t("common:validation.emailInvalid"),
                        },
                      })}
                      placeholder={t("admin:userForm.emailPlaceholder")}
                      size="lg"
                      variant="glass"
                      type="email"
                    />
                  </Field>

                  <Field
                    invalid={!!errors.full_name}
                    errorText={errors.full_name?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("admin:userForm.fullName")}
                    </Text>
                    <VisionInput
                      {...register("full_name")}
                      placeholder={t("admin:userForm.fullNamePlaceholder")}
                      size="lg"
                      variant="glass"
                    />
                  </Field>

                  <Field
                    required={!isEditMode}
                    invalid={!!errors.password}
                    errorText={errors.password?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("admin:userForm.password")}{" "}
                      {isEditMode && "(leave blank to keep current)"}
                    </Text>
                    <VisionInput
                      {...register("password", {
                        required: !isEditMode
                          ? t("common:validation.passwordRequired")
                          : false,
                        minLength: {
                          value: 8,
                          message: t("common:validation.passwordMinLength"),
                        },
                      })}
                      placeholder={t("admin:userForm.passwordPlaceholder")}
                      size="lg"
                      variant="glass"
                      type="password"
                    />
                  </Field>

                  {(!isEditMode || password) && (
                    <Field
                      required={!isEditMode || !!password}
                      invalid={!!errors.password_confirm}
                      errorText={errors.password_confirm?.message}
                    >
                      <Text
                        color={sc.heading}
                        fontSize="sm"
                        fontWeight="500"
                        mb={2}
                      >
                        {t("admin:userForm.confirmPassword")}
                      </Text>
                      <VisionInput
                        {...register("password_confirm", {
                          required:
                            !isEditMode || password
                              ? t("common:validation.passwordConfirmRequired")
                              : false,
                          validate: (value) =>
                            !password ||
                            value === password ||
                            t("common:validation.passwordMismatch"),
                        })}
                        placeholder={t("admin:userForm.confirmPlaceholder")}
                        size="lg"
                        variant="glass"
                        type="password"
                      />
                    </Field>
                  )}
                </VStack>

                <HStack gap={3} w="100%">
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate({ to: "/user-management" })}
                  >
                    {t("common:actions.cancel")}
                  </VisionButton>
                  <VisionButton
                    type="submit"
                    variant="brand"
                    size="lg"
                    flex="1"
                    loading={
                      createUserMutation.isPending ||
                      updateUserMutation.isPending
                    }
                  >
                    {createUserMutation.isPending ||
                    updateUserMutation.isPending
                      ? isEditMode
                        ? t("common:status.updating")
                        : t("common:status.creating")
                      : isEditMode
                        ? t("common:actions.update")
                        : t("common:actions.create")}
                  </VisionButton>
                </HStack>
              </VStack>
            </VisionCard>
          </GradientBorder>
        </Box>
        <VStack gap={6} align="stretch">
          <VisionCard>
            <Text color={sc.heading} fontSize="sm" fontWeight="600" mb={3}>
              {t("admin:userForm.accountSettings")}
            </Text>
            <VStack align="start" gap={3}>
              <Checkbox
                {...register("is_active")}
                colorScheme="brand"
                checked={watch("is_active")}
              >
                <VStack align="start" gap={0}>
                  <Text color={sc.heading} fontSize="sm">
                    {t("admin:userForm.isActive")}
                  </Text>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:userForm.isActiveDesc")}
                  </Text>
                </VStack>
              </Checkbox>
              <Checkbox
                checked={isSuperuser}
                onCheckedChange={({ checked }: { checked: boolean }) => {
                  if (!checked && isSuperuser) {
                    setConfirmUnsuperuser(true)
                  } else {
                    setValue("is_superuser", !!checked, { shouldDirty: true })
                  }
                }}
                colorScheme="purple"
              >
                <VStack align="start" gap={0}>
                  <Text color={sc.heading} fontSize="sm">
                    {t("admin:userForm.isSuperuser")}
                  </Text>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:userForm.isSuperuserDesc")}
                  </Text>
                </VStack>
              </Checkbox>
            </VStack>
          </VisionCard>

          {isEditingSelf && (
            <VisionCard>
              <Appearance />
            </VisionCard>
          )}
        </VStack>
      </Grid>

      <ConfirmDialog
        open={confirmUnsuperuser}
        onClose={() => setConfirmUnsuperuser(false)}
        onConfirm={() => {
          setValue("is_superuser", false, { shouldDirty: true })
          setConfirmUnsuperuser(false)
        }}
        title={t("admin:userForm.removeSuperuserTitle")}
        message={t("admin:userForm.removeSuperuserWarning")}
      />
    </Box>
  )
}
